import { useEffect, useState } from 'react'
import { Button, Card, CardBody, CardFooter, CardHeader, Input, Select, SkeletonList, Switch } from '@/components/ui'
import { QueryBoundary } from '@/components/domain'
import { useT } from '@/internationalization/i18n'
import { useSessionStore } from '@/stores'
import { useProfileSettings, useUpdateProfilePreference } from '../hooks'
import type { ProfileSettings as Profile } from '@/types'

const TIME_ZONES = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Europe/London', label: 'Europe/London' },
  { value: 'America/New_York', label: 'America/New_York' },
]

/**
 * The first card is a form (Save button); the second is switches that apply
 * immediately. Mixing the two inside one card is what makes users unsure
 * whether a change took.
 */
export function ProfileSettings() {
  const t = useT()
  const { query, save } = useProfileSettings()
  const updatePreference = useUpdateProfilePreference()
  // Admin-only, deliberately narrower than the nav gate: everyone else -- owner
  // included -- still sees their name, they just cannot edit it here. The API is
  // unchanged.
  const role = useSessionStore((s) => s.user?.role)
  const canEditName = role === 'owner' || role === 'admin'
  const [draft, setDraft] = useState<Profile | null>(null)

  // Seed the form once the server value arrives, and re-seed if it changes
  // underneath us (another tab, a role change). Editing is local until Save.
  useEffect(() => {
    if (query.data) setDraft(query.data)
  }, [query.data])

  return (
    <QueryBoundary
      isLoading={query.isPending || !draft}
      error={query.error}
      onRetry={query.refetch}
      skeleton={<SkeletonList rows={4} className="sb-panel p-4" />}
    >
      {draft && (
        <div className="space-y-4">
          <Card>
            <CardHeader title={t('profile.card.title')} description={t('profile.card.description')} />
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t('profile.field.name')}
                value={draft.name}
                readOnly={!canEditName}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
              <Input
                label={t('profile.field.email')}
                value={draft.email}
                disabled
                hint={t('profile.field.email.hint')}
              />
              <Select
                label={t('profile.field.timezone')}
                options={TIME_ZONES}
                value={draft.timeZone}
                onChange={(event) => setDraft({ ...draft, timeZone: event.target.value })}
                hint={t('profile.field.timezone.hint')}
              />
              <Select
                label={t('profile.field.language')}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'hi', label: 'हिन्दी' },
                ]}
                value={draft.interfaceLanguage}
                onChange={(event) =>
                  setDraft({ ...draft, interfaceLanguage: event.target.value as Profile['interfaceLanguage'] })
                }
                hint={t('profile.field.language.hint')}
              />
            </CardBody>
            <CardFooter>
              <Button
                variant="primary"
                isLoading={save.isPending}
                onClick={() =>
                  save.mutate({
                    name: draft.name,
                    timeZone: draft.timeZone,
                    interfaceLanguage: draft.interfaceLanguage,
                  })
                }
              >
                {t('common.saveChanges')}
              </Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader
              title={t('profile.personalEmail.title')}
              description={t('profile.personalEmail.description')}
            />
            <CardBody className="space-y-4">
              <Switch
                label={t('profile.dailyDigest.label')}
                description={t('profile.dailyDigest.description')}
                showLabel
                checked={draft.dailyDigest}
                onChange={(dailyDigest) => {
                  setDraft({ ...draft, dailyDigest })
                  updatePreference.mutate({ dailyDigest })
                }}
              />
              <Switch
                label={t('profile.actionAssigned.label')}
                description={t('profile.actionAssigned.description')}
                showLabel
                checked={draft.emailOnActionAssigned}
                onChange={(emailOnActionAssigned) => {
                  setDraft({ ...draft, emailOnActionAssigned })
                  updatePreference.mutate({ emailOnActionAssigned })
                }}
              />
            </CardBody>
          </Card>
        </div>
      )}
    </QueryBoundary>
  )
}
